import { z } from 'zod';
import {
  HEALTH_INSURER_NAME_REQUIRED,
  HEALTH_INSURER_NUMBER_REQUIRED,
} from '@pages/main/patient/constants/validationMessages';

const formatLeadingZeros = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return trimmed;
  if (/^0+$/.test(trimmed)) return '0';
  return trimmed.replace(/^0+/, '');
};

export const insurerFormSchema = z.object({
  insurer_number: z
    .string()
    .trim()
    .min(1, HEALTH_INSURER_NUMBER_REQUIRED)
    .max(100, '保険者番号は100文字以下にしてください。'),
  sub_number: z
    .string()
    .trim()
    .min(1, '枝番は必須です。')
    .superRefine((value, ctx) => {
      if (!/^\d+$/.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '枝番は整数にしてください。' });
        return;
      }
      if (value.length > 9) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '枝番は1〜9桁にしてください。' });
      }
    }),
  insurer_type_id: z.coerce.number().int('保険種別は必須です。'),
  insurance_kind_id: z.coerce.number().int('保険制度は必須です。'),
  insurer_name_kana: z
    .string()
    .trim()
    .max(100, '保険者仮名は100文字以下にしてください。')
    .refine(
      value => !value || /^[\u30A0-\u30FF\u3000\s\uFF08\uFF09()]*$/.test(value),
      '保険者仮名の書式が正しくありません。',
    )
    .optional(),
  insurer_name: z
    .string()
    .trim()
    .min(1, HEALTH_INSURER_NAME_REQUIRED)
    .max(100, '保険者名称は100文字以下にしてください。'),
  staff_department: z
    .string()
    .trim()
    .max(100, '部署名は100文字以下にしてください。')
    .optional(),
  delegation_insurer_name: z
    .string()
    .trim()
    .max(100, '受領委任払い用名称は100文字以下にしてください。')
    .optional(),
  postal_code1: z
    .string()
    .trim()
    .min(1, '郵便番号は必須です。')
    .refine(value => /^\d{3}$/.test(value), '郵便番号は数字にしてください。'),
  postal_code2: z
    .string()
    .trim()
    .min(1, '郵便番号は必須です。')
    .refine(value => /^\d{4}$/.test(value), '郵便番号は数字にしてください。'),
  address1: z
    .string()
    .trim()
    .min(1, '住所1は必須です。')
    .max(100, '住所1は100文字以下で入力してください。'),
  address2: z
    .string()
    .trim()
    .min(1, '住所2は必須です。')
    .max(100, '住所2は100文字以下で入力してください。'),
  address3: z
    .string()
    .trim()
    .max(100, '住所3は100文字以下で入力してください。')
    .optional()
    .nullable(),
  telephone: z
    .string()
    .trim()
    .max(13, '電話番号は13文字以下で入力してください。')
    .optional(),
  available_from: z.string().trim().min(1, '適用開始日は必須です。'),
  available_to: z.string().trim().optional().nullable(),
  cost_rate: z.preprocess(
    formatLeadingZeros,
    z
      .string()
      .trim()
      .optional()
      .superRefine((value, ctx) => {
        if (!value) return;
        if (!/^\d+$/.test(value)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '本人負担は整数にしてください。' });
          return;
        }
        if (Number(value) < 0 || Number(value) > 3) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '0〜3の整数を入力してください。' });
        }
      })
  ),
  family_cost_rate: z.preprocess(
    formatLeadingZeros,
    z
      .string()
      .trim()
      .optional()
      .superRefine((value, ctx) => {
        if (!value) return;
        if (!/^\d+$/.test(value)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '家族負担は整数にしてください。' });
          return;
        }
        if (Number(value) < 0 || Number(value) > 3) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '0〜3の整数を入力してください。' });
        }
      })
  ),
  is_special: z.coerce.number().int().optional(),
  is_enabled_invoice: z.coerce.number().int().optional().nullable(),
  is_nonoutput_rezept: z.coerce.number().int().optional(),
  is_partial_aid: z.coerce.number().int().optional(),
  medix_claim_type_id: z.coerce.number().int().optional(),
  medix_visit_detail_report_id: z.coerce.number().int().optional(),
  upsystem_visit_detail_report_id: z.coerce.number().int().optional(),
  medix_attachement_document_id: z.coerce.number().int().optional(),
  upsystem_attachement_document_id: z.coerce.number().int().optional(),
  medix_visit_report_document_id: z.coerce.number().int().optional(),
  upsystem_visit_report_document_id: z.coerce.number().int().optional(),
  medix_visit_detail_report_delegation_id: z.coerce.number().int().optional(),
  upsystem_visit_detail_report_delegation_id: z.coerce.number().int().optional(),
  is_receipt_delegation: z.coerce.number().int().optional(),
  receipt_delegation_from: z.string().trim().optional(),
  receipt_delegation_to: z.string().trim().optional(),
  calc_type: z.coerce
    .number()
    .int()
    .optional()
    .nullable(),
  limit_amount: z.preprocess(
    formatLeadingZeros,
    z
      .string()
      .trim()
      .optional()
      .superRefine((value, ctx) => {
        if (!value) return;
        if (!/^\d+$/.test(value)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '負担金は整数にしてください。' });
          return;
        }
        if (value.length > 9) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '負担金は1〜9桁にしてください。' });
        }
      })
  ),
  limit_num_month: z.preprocess(
    formatLeadingZeros,
    z
      .string()
      .trim()
      .optional()
      .superRefine((value, ctx) => {
        if (!value) return;
        if (!/^\d+$/.test(value)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '月間上限回数にしてください。' });
          return;
        }
        if (value.length > 2) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '月間上限回数は1〜2桁にしてください。' });
          return;
        }
        if (Number(value) < 0 || Number(value) > 31) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '0〜31の整数を入力してください。' });
        }
      })
  ),
  limit_amount_month: z.preprocess(
    formatLeadingZeros,
    z
      .string()
      .trim()
      .optional()
      .superRefine((value, ctx) => {
        if (!value) return;
        if (!/^\d+$/.test(value)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '月間上限金額は整数にしてください。' });
          return;
        }
        if (value.length > 9) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: '月間上限金額は1〜9桁にしてください。' });
        }
      })
  ),
  is_adopted_lower_cost: z.coerce.number().int().optional(),
});

export type InsurerFormData = z.infer<typeof insurerFormSchema>;
